import { FormEvent, useEffect, useState } from 'react';
import { ArrowDownRight, ArrowRight, Check, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { getSeoForPath } from '../data/seo';
import { usePageSeo } from '../hooks/usePageSeo';
import './AutomotiveConsulting.css';

const pageSeo = getSeoForPath('/conseil-auto');

const costFactors = [
  'Prix et financement',
  'Dépréciation',
  'Assurance',
  'Entretien et pneus',
  'Consommation',
  'Taxes et imprévus',
];

const method = [
  {
    number: '01',
    title: 'Cadrer',
    text: 'Budget, trajets, kilométrage, horizon de détention, contraintes familiales et compromis acceptables.',
  },
  {
    number: '02',
    title: 'Comparer',
    text: 'Générations, motorisations, coût global prévisible, retours d’usage et réalité du marché.',
  },
  {
    number: '03',
    title: 'Classer',
    text: 'Trois à cinq options hiérarchisées, avec les raisons du choix, les concessions et le mode d’acquisition pertinent.',
  },
  {
    number: '04',
    title: 'Décider',
    text: 'Un dossier lisible, des annonces repères, les questions à poser et un débrief de 30 minutes.',
  },
];

const faqs = [
  {
    question: 'Pourquoi payer alors que ChatGPT et Reddit sont gratuits ?',
    answer:
      'Parce que l’information gratuite reste à cadrer, trier, vérifier et pondérer selon votre situation. La Bonne Voiture ne vend pas un accès à des données secrètes : vous déléguez une recherche structurée et obtenez une décision argumentée.',
  },
  {
    question: 'Pouvez-vous aussi m’aider à choisir entre achat, crédit, LOA ou LLD ?',
    answer:
      'Oui. Le dossier peut comparer les avantages, les contraintes et le coût global des différents modes d’acquisition selon votre kilométrage, votre apport et la durée pendant laquelle vous souhaitez garder la voiture. Aucun financement n’est commercialisé.',
  },
  {
    question: 'Est-ce une inspection mécanique ?',
    answer:
      'Non. La mission intervient en amont pour choisir les bons modèles et identifier les contrôles importants. Elle ne remplace ni un essai, ni l’historique du véhicule, ni une inspection physique par un professionnel.',
  },
  {
    question: 'Y a-t-il une commission sur la voiture achetée ?',
    answer:
      'Non. Le prix est fixe et payé uniquement par vous. Aucun vendeur, garage ou site d’annonces ne rémunère la recommandation.',
  },
  {
    question: 'Quels projets correspondent à l’offre pilote ?',
    answer:
      'Principalement les véhicules d’occasion courants entre 15 000 € et 35 000 €. Les véhicules de collection, imports complexes et modèles très haut de gamme demandent un cadrage séparé.',
  },
];

const AutomotiveConsulting = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    budget: '',
    acquisition: '',
    usage: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  usePageSeo(pageSeo);

  useEffect(() => {
    const previousLang = document.documentElement.lang;
    const favicon = document.head.querySelector<HTMLLinkElement>('link[rel="icon"]');
    const previousFavicon = favicon?.href;

    document.documentElement.lang = 'fr';
    document.body.classList.add('labonnevoiture-body');
    favicon?.setAttribute('href', '/assets/la-bonne-voiture-mark.svg');

    return () => {
      document.documentElement.lang = previousLang;
      document.body.classList.remove('labonnevoiture-body');
      if (favicon && previousFavicon) favicon.href = previousFavicon;
    };
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    const toastId = toast.loading('Envoi de votre demande…');

    try {
      await api.post('/contact', {
        name: formData.name,
        email: formData.email,
        subject: 'La Bonne Voiture — Demande de conseil automobile',
        message: `Budget : ${formData.budget}\nMode d’acquisition envisagé : ${
          formData.acquisition || 'À définir'
        }\n\nUsage et besoin :\n${formData.usage}`,
      });
      toast.success('Demande envoyée. Je reviens vers vous rapidement.', { id: toastId });
      setFormData({ name: '', email: '', budget: '', acquisition: '', usage: '' });
    } catch {
      toast.error('Impossible d’envoyer la demande pour le moment.', { id: toastId });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="la-bonne-voiture-page">
      <header className="pm-header">
        <a className="pm-brand" href="#top" aria-label="La Bonne Voiture, accueil">
          <img src="/assets/la-bonne-voiture-mark.svg" alt="" width="38" height="38" />
          <span>La Bonne Voiture</span>
        </a>
        <nav className="pm-nav" aria-label="Navigation principale">
          <a href="#cout-reel">Coût réel</a>
          <a href="#methode">Méthode</a>
          <a href="#faq">FAQ</a>
        </nav>
        <a className="pm-header-cta" href="#contact">
          Décrire mon besoin
          <ArrowDownRight size={17} aria-hidden="true" />
        </a>
      </header>

      <main id="top">
        <section className="pm-hero" aria-labelledby="pm-hero-title">
          <div className="pm-hero-copy">
            <p className="pm-kicker">
              <span />
              Conseil automobile indépendant
            </p>
            <h1 id="pm-hero-title">
              La voiture qui colle à votre vie.
              <em>Et à votre budget réel.</em>
            </h1>
            <p className="pm-hero-intro">
              Une shortlist personnalisée et un avis sur le mode d’acquisition, construits à
              partir de vos usages, de votre budget et des compromis que vous êtes réellement
              prêt à faire.
            </p>
            <div className="pm-hero-actions">
              <a className="pm-button pm-button-primary" href="#contact">
                Lancer ma recherche
                <ArrowRight size={18} aria-hidden="true" />
              </a>
              <a className="pm-text-link" href="#cout-reel">
                Voir ce que je compare
              </a>
            </div>
            <dl className="pm-hero-facts">
              <div>
                <dt>Shortlist</dt>
                <dd>3–5 options</dd>
              </div>
              <div>
                <dt>Lecture</dt>
                <dd>Coût global</dd>
              </div>
              <div>
                <dt>Offre pilote</dt>
                <dd>249 €</dd>
              </div>
            </dl>
          </div>

          <div className="pm-hero-visual">
            <img
              src="/assets/la-bonne-voiture-hero.webp"
              alt="Une voiture familiale argentée sur une zone d’essai traversée par une ligne bleue"
              width="1536"
              height="1024"
              loading="eager"
              decoding="async"
            />
            <div className="pm-image-index" aria-hidden="true">
              LBV / 001
            </div>
            <div className="pm-image-note">
              <span>Objectif</span>
              <strong>Choisir avant de chercher</strong>
            </div>
          </div>
        </section>

        <div className="pm-manifesto" aria-label="Manifeste">
          <span>Votre usage, pas un stock.</span>
          <span>Votre coût, pas une mensualité.</span>
        </div>

        <section id="cout-reel" className="pm-section pm-stakes">
          <div className="pm-stakes-heading">
            <p className="pm-section-label">Le vrai budget automobile</p>
            <h2>Le prix affiché n’est que le début du calcul.</h2>
          </div>

          <div className="pm-stakes-copy">
            <p>
              Les transports absorbent <strong>12,7 %</strong> de la dépense de consommation
              des ménages en 2024. Une voiture engage donc l’un des grands budgets du foyer,
              souvent pendant plusieurs années.
            </p>
            <p>
              Dans le même temps, la LOA représentait près de <strong>87 %</strong> des
              opérations de financement spécialisé de voitures neuves recensées en 2024,
              hors prêts personnels. Sa popularité ne signifie pas qu’elle corresponde à
              tous les usages.
            </p>
          </div>

          <div className="pm-cost-breakdown">
            <p>
              Pour comparer deux voitures correctement, je regarde l’ensemble de ce qui sera
              réellement payé ou perdu pendant la détention :
            </p>
            <ul>
              {costFactors.map((factor) => (
                <li key={factor}>{factor}</li>
              ))}
            </ul>
          </div>

          <p className="pm-source-note">
            Sources :
            {' '}
            <a href="https://www.insee.fr/fr/statistiques/8612568" target="_blank" rel="noreferrer">
              Insee, dépenses des ménages 2024
            </a>
            {' · '}
            <a
              href="https://asf-france.com/wp-content/uploads/2025/03/Eqpmt-Part-An24.pdf"
              target="_blank"
              rel="noreferrer"
            >
              ASF, financements 2024
            </a>
            {' · '}
            <a
              href="https://www.inc-conso.fr/content/location-avec-option-dachat-mode-demploi"
              target="_blank"
              rel="noreferrer"
            >
              Institut national de la consommation
            </a>
          </p>
        </section>

        <section id="pourquoi" className="pm-section pm-problem">
          <div className="pm-section-label">Pourquoi déléguer</div>
          <div className="pm-problem-copy">
            <h2>Plus d’information ne crée pas toujours une meilleure décision.</h2>
            <p>
              Motorisations, niveaux de finition, packs d’options, millésimes, garanties,
              fiscalité, restrictions de circulation et financement : l’offre est pléthorique
              et les comparaisons sont rarement faites à périmètre égal.
            </p>
            <p>
              Comparateurs, vidéos, forums et IA donnent des pistes. Mais lorsque tout semble
              possible, les critères importants se mélangent : usage réel, fiabilité,
              agrément, budget global et disponibilité.
            </p>
          </div>
          <div className="pm-pullquote">
            <span>Les outils gratuits donnent des pistes.</span>
            <strong>La Bonne Voiture prend la responsabilité de la synthèse.</strong>
          </div>
        </section>

        <section id="conseiller" className="pm-advisor">
          <div className="pm-advisor-statement">
            <p>Aucune marque. Aucun vendeur. Aucune commission.</p>
            <blockquote>
              « Je ne vous oriente pas vers ce que j’ai à vendre. Je n’ai aucune voiture à
              placer. »
            </blockquote>
          </div>
          <div className="pm-advisor-copy">
            <p className="pm-section-label">Votre conseiller</p>
            <h2>Passionné d’automobile. Indépendant par construction.</h2>
            <p>
              Nous définissons vos critères lors d’un entretien. J’affine ensuite la
              recherche à partir de mes connaissances et de vérifications ciblées, puis je
              hiérarchise les véhicules qui répondent vraiment à votre situation.
            </p>
            <dl>
              <div>
                <dt>Entretien</dt>
                <dd>Vos usages, contraintes, envies et compromis deviennent un cahier des charges clair.</dd>
              </div>
              <div>
                <dt>Recherche</dt>
                <dd>Je confronte les modèles, versions et motorisations à la réalité de votre projet.</dd>
              </div>
              <div>
                <dt>Acquisition</dt>
                <dd>J’éclaire achat comptant, crédit, LOA ou LLD sans commercialiser de financement.</dd>
              </div>
            </dl>
          </div>
        </section>

        <section id="methode" className="pm-section pm-method">
          <div className="pm-section-heading">
            <div className="pm-section-label">La méthode</div>
            <h2>Quatre étapes. Aucun véhicule à placer.</h2>
            <p>
              La recommandation part de votre quotidien et reste indépendante de toute
              plateforme, vendeur ou commission.
            </p>
          </div>
          <ol className="pm-method-list">
            {method.map((step) => (
              <li key={step.number}>
                <span className="pm-step-number">{step.number}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </li>
            ))}
          </ol>
        </section>

        <section id="livrable" className="pm-section pm-deliverable">
          <div className="pm-deliverable-copy">
            <div className="pm-section-label">Le dossier</div>
            <h2>Un dossier fait pour trancher, pas pour impressionner.</h2>
            <p>
              Chaque option est classée, expliquée et replacée dans son contexte. Le dossier
              garde une trace des arbitrages lorsque les annonces et les avis commencent à
              brouiller la décision.
            </p>
            <ul>
              {[
                'Combinaison modèle, génération et motorisation',
                'Comparaison du coût global sur votre durée de détention',
                'Avis sur achat comptant, crédit, LOA ou LLD',
                'Forces, concessions et cas d’usage',
                'Points de vigilance à contrôler',
                'Annonces repères et questions à poser',
              ].map((item) => (
                <li key={item}>
                  <Check size={16} aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="pm-report" aria-label="Aperçu générique du dossier">
            <div className="pm-report-head">
              <div>
                <span>LA BONNE VOITURE / DOSSIER 001</span>
                <strong>Usage mixte — famille</strong>
              </div>
              <span>25–30 K€</span>
            </div>
            {[
              ['01', 'Meilleur équilibre', 'Option recommandée', '92 %'],
              ['02', 'Choix rationnel', 'Budget préservé', '86 %'],
              ['03', 'Alternative plaisir', 'Compromis assumé', '79 %'],
            ].map(([rank, label, title, score]) => (
              <div className="pm-report-row" key={rank}>
                <span>{rank}</span>
                <div>
                  <small>{label}</small>
                  <strong>{title}</strong>
                </div>
                <b>{score}</b>
              </div>
            ))}
            <div className="pm-report-foot">
              Exemple de structure — données et modèles fictifs
            </div>
          </div>
        </section>

        <section className="pm-price">
          <div className="pm-price-main">
            <div className="pm-section-label">Offre pilote</div>
            <div className="pm-price-value">
              <strong>249</strong>
              <span>€ TTC</span>
            </div>
            <p>
              Prix fixe, quel que soit le prix du véhicule. La mission rémunère la recherche
              et la décision, jamais la transaction.
            </p>
          </div>
          <div className="pm-price-includes">
            <p>Inclus dans la mission</p>
            <ul>
              {[
                'Échange de cadrage',
                'Shortlist de 3 à 5 options',
                'Dossier argumenté',
                'Avis sur le mode d’acquisition',
                'Débrief de 30 minutes',
                'Une révision incluse',
              ].map((item) => (
                <li key={item}>
                  <Check size={16} aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
            <a className="pm-button pm-button-lime" href="#contact">
              Vérifier si l’offre me correspond
              <ArrowRight size={18} aria-hidden="true" />
            </a>
          </div>
        </section>

        <section id="faq" className="pm-section pm-faq">
          <div>
            <div className="pm-section-label">Questions fréquentes</div>
            <h2>Ce que La Bonne Voiture fait. Et ce qu’elle ne prétend pas faire.</h2>
          </div>
          <div className="pm-faq-list">
            {faqs.map((faq) => (
              <details key={faq.question}>
                <summary>
                  {faq.question}
                  <span aria-hidden="true">+</span>
                </summary>
                <p>{faq.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section id="contact" className="pm-contact">
          <div className="pm-contact-copy">
            <div className="pm-section-label">Premier échange</div>
            <h2>Décrivez la voiture qu’il vous faut. Même si vous ne la connaissez pas encore.</h2>
            <p>
              Quelques lignes suffisent. Vous recevrez d’abord un retour honnête sur
              l’adéquation de l’offre à votre projet.
            </p>
          </div>
          <form className="pm-form" onSubmit={handleSubmit}>
            <label>
              Votre nom
              <input
                type="text"
                value={formData.name}
                onChange={(event) => setFormData({ ...formData, name: event.target.value })}
                required
                autoComplete="name"
              />
            </label>
            <label>
              Votre email
              <input
                type="email"
                value={formData.email}
                onChange={(event) => setFormData({ ...formData, email: event.target.value })}
                required
                autoComplete="email"
              />
            </label>
            <label>
              Budget approximatif
              <input
                type="text"
                value={formData.budget}
                onChange={(event) => setFormData({ ...formData, budget: event.target.value })}
                placeholder="Ex. 25 000 €"
                required
              />
            </label>
            <label>
              Mode d’acquisition envisagé
              <select
                value={formData.acquisition}
                onChange={(event) =>
                  setFormData({ ...formData, acquisition: event.target.value })
                }
              >
                <option value="">Je ne sais pas encore</option>
                <option value="Achat comptant">Achat comptant</option>
                <option value="Crédit">Crédit</option>
                <option value="LOA">LOA</option>
                <option value="LLD">LLD</option>
              </select>
            </label>
            <label>
              Usage, contraintes, hésitations
              <textarea
                value={formData.usage}
                onChange={(event) => setFormData({ ...formData, usage: event.target.value })}
                rows={5}
                placeholder="Ex. 20 000 km/an, deux enfants, trajets mixtes, hésitation hybride/essence…"
                required
              />
            </label>
            <button className="pm-button pm-button-lime" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="pm-spinner" size={18} aria-hidden="true" />
                  Envoi…
                </>
              ) : (
                <>
                  Envoyer ma demande
                  <ArrowRight size={18} aria-hidden="true" />
                </>
              )}
            </button>
          </form>
        </section>
      </main>

      <footer className="pm-footer">
        <a className="pm-brand" href="#top">
          <img src="/assets/la-bonne-voiture-mark.svg" alt="" width="32" height="32" />
          <span>La Bonne Voiture</span>
        </a>
        <p>Conseil automobile indépendant · Projet pilote</p>
        <a href="#top">Retour en haut ↑</a>
      </footer>
    </div>
  );
};

export default AutomotiveConsulting;
